/**
 * O que faz: Liga o Gens Design System (escopo .gens-escopo) enquanto a pessoa está em
 *            qualquer rota /admin/* ou em páginas já migradas (/ia, /pipeline, /vendas, /dashboard, /follow-up, /configuracoes, /chat) e desliga ao sair,
 *            sem alterar o visual do restante do app.
 * Onde usa: Renderizado uma única vez dentro do <BrowserRouter> em src/App.tsx.
 * Conexões: useLocation (react-router-dom) e src/styles/gens-escopo.css.
 * Tabelas: Nenhuma.
 * Data: 26/09/2026 (página /ia incluída em 26/09/2026; /pipeline, /vendas e /dashboard em 05/10/2026; /follow-up e /configuracoes em 07/10/2026; /chat em 07/10/2026).
 */

import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import '@/styles/gens-escopo.css';

// Classe que redefine as variáveis do shadcn só dentro do escopo (paleta Gens + DM Sans).
const CLASSE_ESCOPO_GENS = 'gens-escopo';

/**
 * Diz se o caminho pertence à área administrativa.
 * Motivo: comparar "/admin" exato ou "/admin/..." evita pegar rotas como "/administrativo".
 */
export function ehRotaAdmin(caminho: string): boolean {
  return caminho === '/admin' || caminho.startsWith('/admin/');
}

// Páginas fora do admin que já foram migradas para o design system.
// Motivo: /ia e /pipeline abrem modais e selects em portal; o escopo no <body> cobre todos eles.
const ROTAS_MIGRADAS = ['/ia', '/pipeline', '/vendas', '/dashboard', '/follow-up', '/configuracoes', '/chat'];

// Classe extra que troca o fundo papel da página por branco, mantendo o restante da paleta.
const CLASSE_FUNDO_BRANCO = 'gens-escopo--fundo-branco';
// Páginas migradas que mantêm fundo branco por decisão do usuário (05/10/2026: /vendas e /dashboard; 07/10/2026: /follow-up, /configuracoes e /chat).
const ROTAS_FUNDO_BRANCO = ['/vendas', '/dashboard', '/follow-up', '/configuracoes', '/chat'];

/** Diz se a página migrada usa fundo branco em vez do papel do design system. */
export function ehRotaComFundoBranco(caminho: string): boolean {
  return ROTAS_FUNDO_BRANCO.some((rota) => caminho === rota || caminho.startsWith(`${rota}/`));
}

/** Diz se o caminho usa o design system Gens (admin ou página já migrada). */
export function ehRotaComEscopoGens(caminho: string): boolean {
  return ehRotaAdmin(caminho) || ROTAS_MIGRADAS.some((rota) => caminho === rota || caminho.startsWith(`${rota}/`));
}

export function EscopoVisualAdmin() {
  const { pathname } = useLocation();
  const estaNoAdmin = ehRotaComEscopoGens(pathname);
  const fundoBranco = estaNoAdmin && ehRotaComFundoBranco(pathname);

  // A classe vai no <body>, e não num <div> da página, porque modais, menus, selects e
  // toasts são renderizados em "portal" direto no <body>; dentro de um <div> eles
  // escapariam do escopo e abririam com a paleta antiga.
  // useLayoutEffect aplica antes da pintura da tela, evitando piscar a paleta antiga.
  useLayoutEffect(() => {
    if (!estaNoAdmin) return;
    document.body.classList.add(CLASSE_ESCOPO_GENS);
    return () => {
      document.body.classList.remove(CLASSE_ESCOPO_GENS);
    };
  }, [estaNoAdmin]);

  // Efeito separado: trocar entre /pipeline (papel) e /vendas (branco) não desliga o escopo inteiro.
  useLayoutEffect(() => {
    if (!fundoBranco) return;
    document.body.classList.add(CLASSE_FUNDO_BRANCO);
    return () => {
      document.body.classList.remove(CLASSE_FUNDO_BRANCO);
    };
  }, [fundoBranco]);

  return null;
}
